// Runtime theme customization: fetches palette from backend and injects CSS vars + Tailwind overrides.
import { api } from "@/lib/api";

const DEFAULT_THEME = {
  primary: "#1E3A8A",
  accent: "#F59E0B",
  success: "#059669",
  danger: "#DC2626",
};

const STYLE_ID = "minepro-theme-vars";

function hexToRgb(hex) {
  const clean = hex.replace("#", "");
  const num = parseInt(clean, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function shade(hex, pct) {
  const { r, g, b } = hexToRgb(hex);
  const f = (c) => Math.max(0, Math.min(255, Math.round(c + (pct < 0 ? c : 255 - c) * pct)));
  return "#" + [f(r), f(g), f(b)].map((v) => v.toString(16).padStart(2, "0")).join("");
}

export function applyTheme(theme) {
  const t = { ...DEFAULT_THEME, ...(theme || {}) };
  const root = document.documentElement;
  root.style.setProperty("--brand-primary", t.primary);
  root.style.setProperty("--brand-primary-hover", shade(t.primary, 0.12));
  root.style.setProperty("--brand-accent", t.accent);
  root.style.setProperty("--brand-success", t.success);
  root.style.setProperty("--brand-danger", t.danger);

  // Inject overrides for the Tailwind classes used across the app.
  let style = document.getElementById(STYLE_ID);
  if (!style) {
    style = document.createElement("style");
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }
  style.textContent = `
    .bg-blue-900 { background-color: var(--brand-primary) !important; }
    .hover\\:bg-blue-800:hover { background-color: var(--brand-primary-hover) !important; }
    .text-blue-900 { color: var(--brand-primary) !important; }
    .border-blue-900 { border-color: var(--brand-primary) !important; }
    .hover\\:text-blue-900:hover { color: var(--brand-primary) !important; }
    .bg-blue-50 { background-color: ${hexToRgba(t.primary, 0.08)} !important; }
    .hover\\:bg-blue-50:hover { background-color: ${hexToRgba(t.primary, 0.10)} !important; }
    .bg-blue-100 { background-color: ${hexToRgba(t.primary, 0.15)} !important; }
    .hover\\:bg-blue-100:hover { background-color: ${hexToRgba(t.primary, 0.18)} !important; }
    .border-blue-200 { border-color: ${hexToRgba(t.primary, 0.25)} !important; }
    .text-amber-500, .text-amber-600 { color: var(--brand-accent) !important; }
    .bg-amber-100 { background-color: ${hexToRgba(t.accent, 0.18)} !important; }
    .text-emerald-600 { color: var(--brand-success) !important; }
    .text-red-500 { color: var(--brand-danger) !important; }
  `;
}

function hexToRgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export async function loadAndApplyTheme() {
  try {
    const { data } = await api.get("/settings/theme");
    applyTheme(data);
  } catch (_err) {
    applyTheme(DEFAULT_THEME);
  }
}

export { DEFAULT_THEME };
